import { useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { JerseyAvatar } from "components/layout/heat/JerseyAvatar";
import { makeStyles } from "components/providers/makeStyles";
import { useEventChannel } from "components/routes/events/id/EventProvider";
import { T } from "components/util/t";
import { GET_EVENT_TEAM_LEADERBOARD } from "graphql/event";
import { useRefocusEffect } from "hooks/useRefocusEffect";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useTableStyles } from "../../commonStyles/tableStyles";
import { useBindToChannelEvent } from "../../../providers/Realtime/RealtimeProvider";

const useStyles = makeStyles(theme => ({
    title: {
        marginBottom: theme.spacing(2)
    },
    card: {
        marginBottom: theme.spacing(4),
        background: theme.palette.background.low,
        padding: theme.spacing(2),
        borderRadius: parseInt(theme.shape.borderRadius) * 2
    },
    table: {
        "& thead th": {
            textTransform: "uppercase",
        },
        "& thead th, & tbody td": {
            borderBottom: 0,
            "&:first-of-type": {
                paddingRight: theme.spacing(2)
            }
        },
        "& tbody td:first-of-type, & tbody td:last-of-type": {
            textAlign: "center"
        }
    },
    team: {
        display: "flex",
        alignItems: "center",
        "&&": {
            width: "100%",
            padding: theme.spacing(1, 0),
        }
    },
    teamName: {
        marginLeft: theme.spacing(1),
        textTransform: "capitalize"
    },
    button: {
        marginTop: theme.spacing(2)
    }
}));

export const EventTeamLeaderboardSnippet = () => {
    const tableClasses = useTableStyles();
    const classes = useStyles();
    const { id } = useParams();
    const { data, refetch } = useQuery(GET_EVENT_TEAM_LEADERBOARD, { variables: { id }, fetchPolicy: "cache-and-network", nextFetchPolicy: "cache-first" }),
        event = data?.event;

    useRefocusEffect(refetch, true);
    useBindToChannelEvent(useEventChannel(), "event-team-leaderboard-updated", refetch);

    if (!event?.teamLeaderboard?.result.length) return null;

    return (
        <section>
            <Typography variant="h5" component="h2" className={classes.title}>
                <T>Club leaderboard</T>
            </Typography>

            <div className={classes.card}>
                <table className={`${tableClasses.table} ${classes.table}`}>
                    <thead>
                        <tr>
                            <th><Typography variant="label2" color="text.secondary"><T>Rank</T></Typography></th>
                            <th><Typography variant="label2" color="text.secondary"><T>Club</T></Typography></th>
                            <th><Typography variant="label2" color="text.secondary"><T>Points</T></Typography></th>
                        </tr>
                    </thead>
                    <tbody>
                        {event.teamLeaderboard.result.slice(0, 3).map(result => (
                            <tr key={result.teamName}>
                                <td><Typography variant="h7">{result.place}</Typography></td>
                                <td className={classes.team}>
                                    <JerseyAvatar alt={result.teamName} />
                                    <div className={classes.teamName}>{result.teamName}</div>
                                </td>
                                <td><Typography variant="h7">{result.total}</Typography></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Button
                    component={Link}
                    to={`/events/${id}/club-leaderboard`}
                    fullWidth
                    className={classes.button}
                    variant="outlined"
                >
                    <T>View full leaderboard</T>
                </Button>
            </div>
        </section>
    );
};
